import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, formatDistanceToNow } from "date-fns";
import { ArrowLeft, CheckCircle2, Clock, UserCheck, RefreshCw, CalendarClock, Pencil, X, History, ExternalLink, UserPlus, Upload, Download, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (v) =>
  v
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(v)
    : "—";

const formatDateTime = value => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : format(date, "dd MMM yyyy, hh:mm a");
};

const STATUS_STYLE = {
  Open: "bg-red-50 text-red-700 border-red-200",
  New: "bg-red-50 text-red-700 border-red-200",
  Contacted: "bg-green-50 text-green-700 border-green-200",
  Active: "bg-cyan-50 text-cyan-700 border-cyan-200",
  Qualified: "bg-green-50 text-green-700 border-green-200",
  Confirmed: "bg-green-50 text-green-700 border-green-200",
  "Closed Won": "bg-green-50 text-green-700 border-green-200",
  Declined: "bg-red-50 text-red-700 border-red-200",
  "Closed Lost": "bg-red-50 text-red-700 border-red-200",
  Closed: "bg-red-50 text-red-700 border-red-200",
  Converted: "bg-green-50 text-green-700 border-green-200",
};

const STATUS_DISPLAY = {
  "Closed Won": "Confirmed",
  Converted: "Confirmed",
  "Closed Lost": "Declined",
  Closed: "Declined",
  Active: "Follow Up/Active",
  New: "Open",
};

const LEAD_STATUSES = ["Open", "Contacted", "Qualified", "Closed Won", "Closed Lost", "Invalid"];
const STATUS_REASONS = {
  Invalid: ["Customer did not Request", "Incorrect Phone", "Other"],
  "Closed Lost": ["Price too high", "Unable to Accommodate Request", "Booked with other Movers", "Customer Cancelled / Postponed moving plan", "Others"],
};

const ACTIVITY_DOT = {
  created:         "bg-blue-500",
  assigned:        "bg-indigo-500",
  converted:       "bg-green-500",
  note:            "bg-gray-400",
  followup:        "bg-teal-500",
  followup_history:"bg-gray-300",
  updated:         "bg-orange-400",
};

// Parses "Key: value | Key: value" into [{key, value}] with date formatting
function parseFollowupParts(message, prefix) {
  const body = message.replace(prefix, "").trim();
  return body.split(" | ").map(part => {
    const sep = part.indexOf(": ");
    if (sep === -1) return { key: "", value: part };
    const key = part.slice(0, sep);
    let value = part.slice(sep + 2);
    if (key === "Date" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      try { value = format(new Date(value + "T00:00:00"), "dd MMM yyyy"); } catch {}
    }
    return { key, value };
  });
}

const DetailField = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">{label}</p>
    <p className="text-sm font-semibold text-[#0F172A]">{value || "—"}</p>
  </div>
);

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const isRM = authUser?.role === "relationship_manager";
  const canAssign = ["admin", "superadmin", "manager", "supervisor"].includes(authUser?.role);
  const currentUserId = authUser?.id || authUser?._id || "";

  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [advancePaid, setAdvancePaid] = useState("0");
  const [converting, setConverting] = useState(false);
  const [convertShiftingDate, setConvertShiftingDate] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [followupForm, setFollowupForm] = useState({ date: "", action: "", comments: "" });
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [booking, setBooking] = useState(null);
  const [cities, setCities] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusReason, setStatusReason] = useState({ open: false, status: "", reason: "", details: "" });
  const [editForm, setEditForm] = useState({});
  const [uploadingQuotation, setUploadingQuotation] = useState(false);
  const [removingQuotation, setRemovingQuotation] = useState(false);

  const fetchAll = async () => {
    try {
      const [leadRes, actRes, usersRes] = await Promise.all([
        axios.get(`${API}/leads/${id}`, { withCredentials: true }),
        axios.get(`${API}/leads/${id}/activities`, { withCredentials: true }),
        axios.get(`${API}/users/relationship-managers`, { withCredentials: true }),
      ]);
      setLead(leadRes.data);
      setActivities(actRes.data);
      setUsers(usersRes.data);
      setSelectedUser(leadRes.data.assigned_to || "");
      setFollowupForm({
        date:     leadRes.data.next_followup_date || "",
        action:   leadRes.data.next_followup_action || "",
        comments: leadRes.data.next_followup_comments || "",
      });
      // If converted, fetch the linked booking
      if (leadRes.data.status === "Converted") {
        try {
          const bRes = await axios.get(`${API}/confirmed-leads?lead_id=${leadRes.data.id}`, { withCredentials: true });
          setBooking(bRes.data[0] || null);
        } catch {}
      }
    } catch {
      toast.error("Failed to load lead details.");
    }
  };

  useEffect(() => { fetchAll(); }, [id]);
  useEffect(() => {
    axios.get(`${API}/cities`, { withCredentials: true }).then(response => setCities(response.data)).catch(() => {});
  }, []);

  const openEdit = () => {
    setEditForm({
      customer_name: lead.customer_name || "",
      phone: lead.phone || "",
      alternate_number: lead.alternate_number || "",
      lead_source: lead.lead_source || "",
      moving_from: lead.moving_from || "",
      moving_to: lead.moving_to || "",
      from_location: lead.from_location || "",
      to_location: lead.to_location || "",
      load_size: lead.load_size || "",
      quotation: lead.quotation || "",
      shifting_date: lead.shifting_date || "",
      additional_remarks: lead.additional_remarks || "",
    });
    setShowEdit(true);
  };

  const saveEdit = async event => {
    event.preventDefault();
    if (!editForm.customer_name?.trim() || !editForm.phone?.trim()) {
      toast.error("Customer name and phone are required.");
      return;
    }
    setSavingEdit(true);
    try {
      const { data } = await axios.patch(`${API}/leads/${id}`, {
        ...editForm,
        quotation: parseFloat(editForm.quotation) || 0,
      }, { withCredentials: true });
      setLead(data);
      const activityResponse = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(activityResponse.data);
      setShowEdit(false);
      toast.success("Lead updated successfully.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update lead."));
    } finally {
      setSavingEdit(false);
    }
  };

  const changeStatus = async (status, comment = "") => {
    if (!status || status === lead.status) return;
    setSavingStatus(true);
    try {
      const { data } = await axios.patch(`${API}/leads/${id}`, { status, comment: comment || undefined }, { withCredentials: true });
      setLead(data);
      const activityResponse = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(activityResponse.data);
      toast.success(`Status updated to ${STATUS_DISPLAY[status] || status}.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update status."));
    } finally {
      setSavingStatus(false);
    }
  };

  const requestStatusChange = status => {
    if (status === "Closed Lost" || status === "Invalid") {
      setStatusReason({ open: true, status, reason: "", details: "" });
      return;
    }
    changeStatus(status);
  };

  const submitStatusReason = async event => {
    event.preventDefault();
    if (!statusReason.reason) return toast.error("Please select a reason.");
    const needsDetails = statusReason.reason === "Other" || statusReason.reason === "Others";
    if (needsDetails && !statusReason.details.trim()) return toast.error("Please mention the reason.");
    const label = statusReason.status === "Invalid" ? "Invalid" : "Declined";
    const reason = needsDetails ? `${statusReason.reason}: ${statusReason.details.trim()}` : statusReason.reason;
    const status = statusReason.status;
    setStatusReason(current => ({ ...current, open: false }));
    await changeStatus(status, `Reason for ${label}: ${reason}`);
  };

  const handleAssign = async () => {
    if (!selectedUser) return toast.error("Please select a user to assign.");
    const user = users.find((u) => u.id === selectedUser);
    if (!user) return;
    setAssigning(true);
    try {
      const { data } = await axios.patch(
        `${API}/leads/${id}/assign`,
        { user_id: user.id, user_name: user.name },
        { withCredentials: true }
      );
      setLead(data);
      const actRes = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(actRes.data);
      toast.success(`Lead assigned to ${user.name}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to assign lead."));
    } finally {
      setAssigning(false);
    }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    setConverting(true);
    try {
      const { data } = await axios.post(
        `${API}/leads/${id}/convert-to-booking`,
        { advance_paid: parseFloat(advancePaid) || 0, shifting_date: convertShiftingDate, special_requests: specialRequests },
        { withCredentials: true }
      );
      setLead(data);
      const actRes = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(actRes.data);
      setShowConvert(false);
      setAdvancePaid("0");
      setSpecialRequests("");
      toast.success("Lead successfully converted to booking!");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to convert to confirmed lead."));
    } finally {
      setConverting(false);
    }
  };

  const handleFollowupSave = async () => {
    setSavingFollowup(true);
    try {
      const { data } = await axios.patch(
        `${API}/leads/${id}/followup`,
        {
          next_followup_date:     followupForm.date,
          next_followup_action:   followupForm.action,
          next_followup_comments: followupForm.comments,
        },
        { withCredentials: true }
      );
      setLead(data);
      setFollowupForm({
        date:     data.next_followup_date || "",
        action:   data.next_followup_action || "",
        comments: data.next_followup_comments || "",
      });
      const actRes = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(actRes.data);
      toast.success("Follow-up updated.");
    } catch {
      toast.error("Failed to update follow-up.");
    } finally {
      setSavingFollowup(false);
    }
  };

  const handleAssignToMyself = async () => {
    try {
      const { data } = await axios.patch(`${API}/leads/${id}/assign-to-me`, {}, { withCredentials: true });
      setLead(data);
      const actRes = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(actRes.data);
      toast.success("Lead assigned to you. You can now update follow-ups.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to assign lead."));
    }
  };

  const uploadQuotation = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Quotation must be 10 MB or smaller.");
      return;
    }
    const formData = new FormData();
    formData.append("quotation", file);
    setUploadingQuotation(true);
    try {
      const { data } = await axios.post(`${API}/leads/${id}/quotation`, formData, { withCredentials: true });
      setLead(data);
      const actRes = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(actRes.data);
      toast.success("Quotation uploaded.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to upload quotation."));
    } finally {
      setUploadingQuotation(false);
    }
  };

  const downloadQuotation = async () => {
    try {
      const response = await axios.get(`${API}/leads/${id}/quotation/download`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = lead.quotation_file_name || "quotation";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to download quotation."));
    }
  };

  const removeQuotation = async () => {
    if (!window.confirm("Are you sure you want to remove this quotation?")) return;
    setRemovingQuotation(true);
    try {
      const { data } = await axios.delete(`${API}/leads/${id}/quotation`, { withCredentials: true });
      setLead(data);
      const actRes = await axios.get(`${API}/leads/${id}/activities`, { withCredentials: true });
      setActivities(actRes.data);
      toast.success("Quotation removed.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to remove quotation."));
    } finally {
      setRemovingQuotation(false);
    }
  };

  if (!lead) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex items-center gap-2 text-[#475569] text-sm">
          <RefreshCw size={16} className="animate-spin" /> Loading...
        </div>
      </div>
    );
  }

  const isConverted = lead.status === "Converted";
  // For Relationship Managers: can only edit if the lead is assigned to them
  const isMyLead    = !isRM || lead.assigned_to === currentUserId;
  const canEdit     = !isConverted && isMyLead;

  return (
    <div className="space-y-5 pb-10" data-testid="lead-detail-page">
      {/* ── Back + title ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/leads")}
          data-testid="back-to-leads"
          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#475569] hover:text-[#1B2A6B] transition-colors"
        >
          <ArrowLeft size={14} /> Back to Leads
        </button>
        <span className="text-[#E2E8F0]">/</span>
        <span className="text-sm font-semibold text-[#0F172A]">{lead.customer_name}</span>
        <span className={`ml-auto inline-flex items-center px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_STYLE[lead.status] || STATUS_STYLE.New}`}>
          {STATUS_DISPLAY[lead.status] || lead.status}
        </span>
        {canEdit && (
          <Button onClick={openEdit} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold uppercase tracking-widest text-xs h-9 px-4 border-b-2 border-[#FFD700]">
            <Pencil size={12} className="mr-1.5" />Edit Lead
          </Button>
        )}
      </div>

      {/* ── Quotation document ── */}
      <div className="bg-white border border-[#E2E8F0]">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
          <FileText size={16} className="text-[#1B2A6B]" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569]">Quotation Document</h3>
        </div>
        <div className="p-6">
          {lead.quotation_file_path ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#0F172A]">{lead.quotation_file_name || "Quotation"}</p>
                <p className="mt-1 text-xs text-[#64748B]">
                  {lead.quotation_uploaded_at ? `Uploaded ${formatDateTime(lead.quotation_uploaded_at)}` : "Quotation available"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button type="button" onClick={downloadQuotation} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white text-xs font-bold uppercase tracking-wider">
                  <Download size={14} className="mr-1.5" />Download
                </Button>
                {authUser?.role === "superadmin" && (
                  <Button type="button" variant="outline" disabled={removingQuotation} onClick={removeQuotation} className="rounded-none border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold uppercase tracking-wider">
                    <Trash2 size={14} className="mr-1.5" />{removingQuotation ? "Removing..." : "Remove"}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <label className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed border-[#CBD5E1] px-6 py-8 text-center hover:border-[#1B2A6B] hover:bg-[#F8FAFC] ${uploadingQuotation ? "pointer-events-none opacity-60" : ""}`}>
              <Upload size={22} className="mb-2 text-[#1B2A6B]" />
              <span className="text-sm font-semibold text-[#0F172A]">{uploadingQuotation ? "Uploading quotation..." : "Upload Quotation"}</span>
              <span className="mt-1 text-xs text-[#64748B]">PDF, Word, JPG or PNG · Maximum 10 MB</span>
              <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" disabled={uploadingQuotation} onChange={uploadQuotation} />
            </label>
          )}
        </div>
      </div>

      {/* ── Assign-to-Myself banner (RM viewing another RM's lead) ── */}
      {isRM && !isMyLead && !isConverted && (
        <div className="flex items-center justify-between bg-[#EEF2FF] border border-[#C7D2FE] px-5 py-3">
          <div className="flex items-center gap-2.5">
            <UserPlus size={16} className="text-[#1B2A6B] shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#1B2A6B]">
                {lead.assigned_to_name
                  ? `Currently assigned to ${lead.assigned_to_name}`
                  : "This lead is not assigned to you"}
              </p>
              <p className="text-xs text-[#475569]">Assign it to yourself to start updating follow-ups</p>
            </div>
          </div>
          <Button
            data-testid="assign-to-myself-btn"
            onClick={handleAssignToMyself}
            className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-5 border-b-2 border-[#FFD700] shrink-0"
          >
            Assign to Myself
          </Button>
        </div>
      )}

      {/* ── Lead details card ── */}
      <div className="bg-white border border-[#E2E8F0]">
        <div className="bg-[#1B2A6B] px-6 py-4 border-l-4 border-[#FFD700]">
          <h2 className="text-lg font-bold text-white">{lead.customer_name}</h2>
          <p className="text-white/60 text-xs mt-0.5">{lead.phone}</p>
        </div>
        <div className="p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-5">
          <DetailField label="Moving From" value={lead.moving_from} />
          <DetailField label="Moving To" value={lead.moving_to} />
          <DetailField label="From Location" value={lead.from_location} />
          <DetailField label="To Location" value={lead.to_location} />
          <DetailField label="Load Size" value={lead.load_size} />
          <DetailField label="Quotation" value={formatINR(lead.quotation)} />
          <DetailField label="Alternate Number" value={lead.alternate_number} />
          <DetailField
            label="Shifting Date"
            value={lead.shifting_date ? format(new Date(lead.shifting_date + "T00:00:00"), "dd MMM yyyy") : null}
          />
          <DetailField label="Assigned To" value={lead.assigned_to_name || "Unassigned"} />
          <DetailField
            label="Next Follow-up"
            value={lead.next_followup_date ? format(new Date(lead.next_followup_date + "T00:00:00"), "dd MMM yyyy") : null}
          />
          <DetailField label="Follow-up Action" value={lead.next_followup_action} />
          {lead.next_followup_comments && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Follow-up Comments</p>
              <p className="text-sm text-[#0F172A]">{lead.next_followup_comments}</p>
            </div>
          )}
          {lead.additional_remarks && (
            <div className="col-span-2 sm:col-span-3 lg:col-span-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-0.5">Additional Remarks</p>
              <p className="text-sm text-[#0F172A]">{lead.additional_remarks}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Two-column: Timeline + Assigned To ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Activity Timeline */}
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] p-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569] mb-5">
            Activity Timeline
          </h3>
          {activities.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-8">No activity yet.</p>
          ) : (
            <div className="space-y-0">
              {activities.map((act, idx) => {
                  const isHistory = act.type === "followup_history";
                  const isUpdated = act.type === "updated";
                  const isFollowup = act.type === "followup";
                  return (
                  <div key={act.id} className="flex gap-4">
                    {/* Dot + line */}
                    <div className="flex flex-col items-center pt-1 shrink-0">
                      <div className={`w-3 h-3 rounded-full shrink-0 ${ACTIVITY_DOT[act.type] || "bg-gray-400"}`} />
                      {idx < activities.length - 1 && (
                        <div className="w-px flex-1 bg-[#E2E8F0] mt-1" />
                      )}
                    </div>
                    {/* Content */}
                    <div className={`pb-6 flex-1 ${idx === activities.length - 1 ? "pb-0" : ""}`}>
                      {isHistory ? (
                        /* Follow-up Archived — structured lines */
                        <div className="bg-[#F9FAFB] border border-[#E2E8F0] px-3 py-2.5">
                          <div className="flex items-center gap-1.5 mb-2">
                            <History size={11} className="text-[#94A3B8]" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Follow-up Archived</span>
                          </div>
                          <div className="space-y-1">
                            {parseFollowupParts(act.message, "Previous follow-up archived — ").map((item, i) => (
                              <p key={i} className="text-xs text-[#64748B] leading-snug">
                                <span className="font-bold text-[#475569]">{item.key}</span>
                                {item.key ? <span className="text-[#94A3B8]"> — </span> : null}
                                {item.value}
                              </p>
                            ))}
                          </div>
                        </div>
                      ) : isFollowup ? (
                        /* Follow-up Updated — structured lines */
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-teal-600 mb-1.5">Follow-up updated</p>
                          <div className="space-y-1">
                            {parseFollowupParts(act.message, "Follow-up updated — ").map((item, i) => (
                              <p key={i} className="text-sm text-[#0F172A] leading-snug">
                                <span className="font-bold">{item.key}</span>
                                {item.key ? <span className="text-[#94A3B8]"> — </span> : null}
                                <span className="font-normal text-[#475569]">{item.value}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      ) : isUpdated ? (
                        /* Lead field changes */
                        <div>
                          <p className="text-sm font-medium text-[#0F172A]">Lead details updated</p>
                          <div className="mt-1 space-y-0.5">
                            {act.message.replace("Lead details updated — ", "").split(" | ").map((change, ci) => (
                              <p key={ci} className="text-xs text-[#475569] font-mono bg-[#F8FAFC] px-2 py-0.5 border-l-2 border-orange-300">{change}</p>
                            ))}
                          </div>
                        </div>
                      ) : (
                        /* Default */
                        <p className="text-sm font-medium text-[#0F172A]">{act.message}</p>
                      )}
                      <div className="flex items-center gap-1.5 mt-1">
                        <Clock size={11} className="text-[#94A3B8]" />
                        <span className="text-[11px] text-[#94A3B8]">
                          {act.created_by_name} ·{" "}
                          {formatDistanceToNow(new Date(act.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                  );
              })}
            </div>
          )}
        </div>

        {/* Assigned To */}
        <div className="bg-white border border-[#E2E8F0] p-6 flex flex-col gap-5">
          <div className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569]">Lead Status</h3>
            {canEdit ? (
              <select
                aria-label="Lead status"
                value={lead.status}
                disabled={savingStatus}
                onChange={event => requestStatusChange(event.target.value)}
                className={`w-full h-10 border px-3 text-xs font-bold uppercase tracking-wider focus:outline-none disabled:opacity-60 ${STATUS_STYLE[lead.status] || STATUS_STYLE.New}`}
              >
                {LEAD_STATUSES.map(status => <option key={status} value={status}>{STATUS_DISPLAY[status] || status}</option>)}
              </select>
            ) : (
              <span className={`inline-flex items-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_STYLE[lead.status] || STATUS_STYLE.New}`}>
                {STATUS_DISPLAY[lead.status] || lead.status}
              </span>
            )}
          </div>
          <div className="border-t border-[#E2E8F0]" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569]">
            Assigned To
          </h3>

          {/* Converted banner */}
          {isConverted && (
            <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 px-3 py-2">
              <CheckCircle2 size={12} className="text-purple-500 shrink-0" />
              <p className="text-xs text-purple-700">Lead converted — editing disabled.</p>
            </div>
          )}

          {/* Current assignment */}
          {lead.assigned_to_name ? (
            <div className="flex items-center gap-3 p-3 bg-[#F9FAFB] border border-[#E2E8F0]">
              <div className="w-9 h-9 rounded-full bg-[#1B2A6B] flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-[#FFD700]">
                  {lead.assigned_to_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                </span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0F172A]">{lead.assigned_to_name}</p>
                <p className="text-[11px] text-[#475569]">Currently assigned</p>
              </div>
              <UserCheck size={16} className="text-green-500 ml-auto" />
            </div>
          ) : (
            <p className="text-sm text-[#94A3B8] italic">Not assigned yet</p>
          )}

          {/* Assignment is available only to supervisors and administrators. */}
          {canAssign && (
            <div className={`space-y-3 ${isConverted ? "opacity-40 pointer-events-none select-none" : ""}`}>
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">
                {lead.assigned_to_name ? "Reassign to" : "Assign to"}
              </p>
              <Select value={selectedUser} onValueChange={setSelectedUser} disabled={isConverted}>
                <SelectTrigger
                  data-testid="assign-user-select"
                  className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-10 text-sm"
                >
                  <SelectValue placeholder="Select a team member" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="rounded-none">
                      <div className="flex flex-col">
                        <span className="font-medium">{u.name}</span>
                        <span className="text-[10px] text-[#94A3B8] capitalize">
                          {u.role?.replace(/_/g, " ")}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                data-testid="assign-btn"
                onClick={handleAssign}
                disabled={assigning || !selectedUser || isConverted}
                className="w-full rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 border-b-2 border-[#FFD700] disabled:opacity-50"
              >
                {assigning ? "Assigning..." : "Assign"}
              </Button>
            </div>
          )}

          {/* ── Next Follow-up ── */}
          <div className={`border-t border-[#E2E8F0] pt-5 space-y-3 ${!canEdit ? "opacity-50 pointer-events-none select-none" : ""}`}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#475569] flex items-center gap-1.5">
              <CalendarClock size={13} className="text-[#1B2A6B]" />
              Next Follow-up
              {!canEdit && isRM && (
                <span className="ml-auto text-[10px] font-normal normal-case tracking-normal text-[#94A3B8]">
                  Assign to yourself to edit
                </span>
              )}
            </h3>
            <div className="space-y-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] block mb-1">Date</label>
                <input
                  type="date"
                  value={followupForm.date}
                  onChange={e => setFollowupForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="followup-date-input"
                  disabled={!canEdit}
                  className="w-full text-sm border border-[#E2E8F0] px-3 h-10 focus:border-[#1B2A6B] focus:outline-none bg-white"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] block mb-1">Action Item</label>
                <input
                  type="text"
                  value={followupForm.action}
                  onChange={e => setFollowupForm(f => ({ ...f, action: e.target.value }))}
                  placeholder="e.g. Call customer to confirm dates"
                  data-testid="followup-action-input"
                  disabled={!canEdit}
                  className="w-full text-sm border border-[#E2E8F0] px-3 h-10 focus:border-[#1B2A6B] focus:outline-none bg-white"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B] block mb-1">Comments</label>
                <textarea
                  value={followupForm.comments}
                  onChange={e => setFollowupForm(f => ({ ...f, comments: e.target.value }))}
                  placeholder="Any additional notes..."
                  data-testid="followup-comments-input"
                  disabled={!canEdit}
                  rows={3}
                  className="w-full text-sm border border-[#E2E8F0] px-3 py-2 focus:border-[#1B2A6B] focus:outline-none bg-white resize-none"
                />
              </div>
              <Button
                data-testid="followup-save-btn"
                onClick={handleFollowupSave}
                disabled={savingFollowup || !canEdit}
                className="w-full rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 border-b-2 border-[#FFD700] disabled:opacity-50"
              >
                {savingFollowup ? "Saving..." : "Save Follow-up"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Convert to Confirmed Lead ── */}
      <div className="flex justify-end gap-3 pt-2">
        {isConverted ? (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-purple-50 border border-purple-200">
            <div className="flex items-center gap-2 text-purple-700 text-xs font-bold uppercase tracking-widest">
              <CheckCircle2 size={14} />
              Converted to Booking
            </div>
            {booking && (
              <>
                <span className="text-purple-200">|</span>
                <button
                  data-testid="view-booking-link"
                  onClick={() => navigate(`/bookings/${booking.id}`)}
                  className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#1B2A6B] hover:underline"
                >
                  <ExternalLink size={12} />
                  View Booking
                </button>
              </>
            )}
          </div>
        ) : isMyLead ? (<>
          {lead.status !== "Closed Lost" && lead.status !== "Closed" && lead.status !== "Declined" && (
            <Button
              type="button"
              data-testid="mark-lead-declined-btn"
              disabled={savingStatus}
              onClick={() => requestStatusChange("Closed Lost")}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white font-bold tracking-widest uppercase text-xs h-11 px-7 border-b-4 border-red-900 disabled:opacity-50"
            >
              {savingStatus ? "Updating..." : "Mark as Declined"}
            </Button>
          )}
          {lead.status !== "Invalid" && (
            <Button
              type="button"
              data-testid="mark-lead-invalid-btn"
              disabled={savingStatus}
              onClick={() => requestStatusChange("Invalid")}
              className="rounded-none bg-slate-600 hover:bg-slate-700 text-white font-bold tracking-widest uppercase text-xs h-11 px-7 border-b-4 border-slate-900 disabled:opacity-50"
            >
              {savingStatus ? "Updating..." : "Mark as Invalid"}
            </Button>
          )}
          <Button
            data-testid="convert-to-booking-btn"
            onClick={() => { setConvertShiftingDate(lead.shifting_date || ""); setShowConvert(true); }}
            className="rounded-none bg-[#FFD700] hover:bg-[#f0c800] text-[#1B2A6B] font-bold tracking-widest uppercase text-xs h-11 px-8 border-b-4 border-[#1B2A6B]"
          >
            Convert to Confirmed Lead
          </Button>
        </>) : null}
      </div>

      <Dialog open={statusReason.open} onOpenChange={open => { if (!open) setStatusReason({ open: false, status: "", reason: "", details: "" }); }}>
        <DialogContent className="max-w-md rounded-none p-0 gap-0">
          <div className={`${statusReason.status === "Invalid" ? "bg-slate-700 border-slate-900" : "bg-red-700 border-red-900"} px-6 py-4 border-b-4`}>
            <DialogTitle className="text-white font-bold tracking-wide text-base">
              {statusReason.status === "Invalid" ? "Mark as Invalid" : "Mark as Declined"}
            </DialogTitle>
            <p className="mt-0.5 text-xs text-white/70">Select a reason before updating this lead.</p>
          </div>
          <form onSubmit={submitStatusReason}>
            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">Reason *</label>
                <select
                  value={statusReason.reason}
                  onChange={event => setStatusReason(current => ({ ...current, reason: event.target.value, details: "" }))}
                  className="h-11 w-full border border-[#E2E8F0] bg-white px-3 text-sm focus:border-[#1B2A6B] focus:outline-none"
                  required
                >
                  <option value="">Select Reason</option>
                  {(STATUS_REASONS[statusReason.status] || []).map(reason => <option key={reason} value={reason}>{reason}</option>)}
                </select>
              </div>
              {(statusReason.reason === "Other" || statusReason.reason === "Others") && <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-[#1B2A6B]">Please Mention Reason *</label>
                <textarea
                  rows={3}
                  value={statusReason.details}
                  onChange={event => setStatusReason(current => ({ ...current, details: event.target.value }))}
                  className="w-full resize-none border border-[#E2E8F0] px-3 py-2 text-sm focus:border-[#1B2A6B] focus:outline-none"
                  placeholder="Enter the reason"
                  required
                />
              </div>}
            </div>
            <DialogFooter className="gap-2 border-t border-[#E2E8F0] bg-[#F9FAFB] px-6 py-4">
              <button type="button" onClick={() => setStatusReason({ open: false, status: "", reason: "", details: "" })} className="h-9 border border-[#CBD5E1] bg-white px-5 text-xs font-bold uppercase tracking-widest text-[#475569]">Cancel</button>
              <Button type="submit" disabled={savingStatus} className={`${statusReason.status === "Invalid" ? "bg-slate-700 hover:bg-slate-800" : "bg-red-700 hover:bg-red-800"} h-9 rounded-none px-5 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50`}>
                Confirm
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Convert to Confirmed Lead Modal ── */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl rounded-none p-0 gap-0 max-h-[90vh] overflow-y-auto">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Edit Lead</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Changes are recorded in the activity timeline</p>
          </div>
          <form onSubmit={saveEdit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-5">
              {[
                ["Customer Name *", "customer_name", "text"], ["Phone *", "phone", "text"],
                ["Alternate Number", "alternate_number", "text"], ["Lead Source", "lead_source", "text"],
                ["From Location", "from_location", "text"], ["To Location", "to_location", "text"],
                ["Load Size", "load_size", "text"], ["Quotation (₹)", "quotation", "number"],
                ["Shifting Date", "shifting_date", "date"],
              ].map(([label, field, type]) => (
                <label key={field} className="space-y-1.5">
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">{label}</span>
                  <Input type={type} value={editForm[field] ?? ""} onChange={event => setEditForm(form => ({ ...form, [field]: event.target.value }))} className="rounded-none border-[#E2E8F0] focus-visible:ring-0 h-10" />
                </label>
              ))}
              {["moving_from", "moving_to"].map(field => (
                <label key={field} className="space-y-1.5">
                  <span className="block text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">{field === "moving_from" ? "Moving From" : "Moving To"}</span>
                  <select value={editForm[field] || ""} onChange={event => setEditForm(form => ({ ...form, [field]: event.target.value }))} className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm focus:outline-none focus:border-[#1B2A6B]">
                    <option value="">Select city</option>
                    {cities.map(city => <option key={city.id} value={city.city_name}>{city.city_name}</option>)}
                  </select>
                </label>
              ))}
              <label className="space-y-1.5 sm:col-span-2">
                <span className="block text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Additional Remarks</span>
                <textarea rows={3} value={editForm.additional_remarks || ""} onChange={event => setEditForm(form => ({ ...form, additional_remarks: event.target.value }))} className="w-full border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:border-[#1B2A6B] resize-none" />
              </label>
            </div>
            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => setShowEdit(false)} className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0]">Cancel</button>
              <Button type="submit" disabled={savingEdit} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold uppercase tracking-widest text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-50">{savingEdit ? "Saving..." : "Save Changes"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showConvert} onOpenChange={(o) => { if (!o) { setAdvancePaid("0"); setConvertShiftingDate(""); } setShowConvert(o); }}>
        <DialogContent className="max-w-sm rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">
              Convert to Confirmed Lead
            </DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">
              {lead.customer_name} · {lead.moving_from} → {lead.moving_to}
            </p>
          </div>

          <form onSubmit={handleConvert}>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-[#475569]">
                This will create a confirmed lead and move the lead to <strong>Converted</strong> status.
              </p>
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">
                  Advance Paid (₹)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569] text-sm font-medium pointer-events-none">
                    ₹
                  </span>
                  <Input
                    type="number"
                    min="0"
                    data-testid="advance-paid-input"
                    value={advancePaid}
                    onChange={(e) => setAdvancePaid(e.target.value)}
                    className="rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm pl-7"
                  />
                </div>
                <p className="text-[11px] text-[#94A3B8]">Enter 0 if no advance has been paid.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">
                  Shifting Date
                </label>
                <input
                  type="date"
                  value={convertShiftingDate}
                  onChange={e => setConvertShiftingDate(e.target.value)}
                  data-testid="convert-shifting-date-input"
                  className="w-full text-sm border border-[#E2E8F0] px-3 h-11 focus:border-[#1B2A6B] focus:outline-none bg-white"
                />
                <p className="text-[11px] text-[#94A3B8]">Pre-filled from lead. Edit if the date has changed.</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">
                  Special Requests
                </label>
                <textarea
                  rows={3}
                  value={specialRequests}
                  onChange={e => setSpecialRequests(e.target.value)}
                  placeholder="Any special instructions or requests for this move…"
                  data-testid="convert-special-requests-input"
                  className="w-full text-sm border border-[#E2E8F0] px-3 py-2.5 focus:border-[#1B2A6B] focus:outline-none bg-white resize-none"
                />
              </div>
            </div>

            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button
                type="button"
                onClick={() => { setAdvancePaid("0"); setShowConvert(false); }}
                data-testid="convert-cancel-btn"
                className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors"
              >
                Cancel
              </button>
              <Button
                type="submit"
                data-testid="convert-confirm-btn"
                disabled={converting}
                className="rounded-none bg-[#FFD700] hover:bg-[#f0c800] text-[#1B2A6B] font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#1B2A6B] disabled:opacity-60"
              >
                {converting ? "Converting..." : "Confirm & Convert"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
